import { useQuery } from '@tanstack/react-query';

import { PlannerConfigEntity } from '@/services/configurator/PlannerConfigTypes';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { CTE_BASE_URL, PROD_BASE_URL } from '@/utils/env';
import Fetch from '@/services/fetch/Fetch';
import { useEffect, useState } from 'react';
import { CheckedState } from '@radix-ui/react-checkbox';
import { isObjectsEqual } from '@/utils/compare-json';

type ListResponse = {
  data: PlannerConfigEntity[];
};

export function ListApproval({
  setOpenModal,
}: {
  setOpenModal: (open: boolean) => void;
}) {
  const [selected, setSelected] = useState<{ [identifier: string]: boolean }>(
    {},
  );
  const [isLoading, setIsLoading] = useState(false);
  const [changedPlannerIds, setChangedPlannerIds] = useState<string[]>([]);

  const { data: cte } = useQuery<ListResponse>({
    queryKey: ['get-planners'],
    queryFn: () => Fetch.json(`${CTE_BASE_URL}/published/list?limit=100000`),
    refetchOnWindowFocus: false,
  });

  const { data: prod } = useQuery<ListResponse>({
    queryKey: ['get-planners-prod'],
    queryFn: () => Fetch.json(`${PROD_BASE_URL}/published/list?limit=100000`),
    refetchOnWindowFocus: false,
  });

  const handleChange = (isChecked: CheckedState, identifier: string) => {
    if (typeof isChecked !== 'boolean') {
      throw new Error('isChecked is not a boolean');
    }

    setSelected((prev) => ({ ...prev, [identifier]: isChecked }));
  };

  const onSubmit = async () => {
    setIsLoading(true);
    const selectedItems = Object.keys(selected).filter(
      (key) => selected[key] === true,
    );

    const selectedItemsData = cte?.data?.filter((item) =>
      selectedItems.includes(item.identifier),
    );

    if (!selectedItems.length || !selectedItemsData) {
      return;
    }

    selectedItemsData.forEach(async (item) => {
      try {
        await Fetch.json(
          `${CTE_BASE_URL}/release/request-prod-approval/${item.id}`,
          { method: 'POST' },
        );
        toast.success(`Sent for approval successfully [${item.identifier}]`);
        setOpenModal(false);
      } catch (error) {
        if (error instanceof Error) {
          toast.error(
            `Failed to update planner ${item.identifier} ${error.stack} ${error.message}`,
          );
        } else {
          toast.error(
            `Failed to update planner ${item.identifier}, unknown error: ${JSON.stringify(error)}`,
          );
        }
      } finally {
        setIsLoading(false);
      }
    });
  };

  useEffect(() => {
    if (!cte || !prod) {
      return;
    }

    const comparePlanners = () => {
      const foundChangedPlannerIds: string[] = [];

      const cteDataClone = [...cte.data];
      const prodDataClone = [...prod.data];

      for (const ctePlanner of cteDataClone) {
        const prodPlanner = prodDataClone.find((p) => {
          return ctePlanner.identifier === p.identifier;
        });

        if (ctePlanner && prodPlanner) {
          // We don't want to diff with lastModified because that's always gonna be different. I think a new date is set on each publish.
          // @ts-expect-error The operand of a 'delete' operator must be optional
          delete ctePlanner.lastModified;
          // @ts-expect-error The operand of a 'delete' operator must be optional
          delete prodPlanner.lastModified;
        }

        if (!isObjectsEqual(ctePlanner, prodPlanner)) {
          foundChangedPlannerIds.push(ctePlanner.identifier);
        }
      }

      setChangedPlannerIds(foundChangedPlannerIds);
    };

    comparePlanners();
  }, [cte, prod]);

  return (
    <div className='overflow-y-auto' style={{ maxHeight: '75vh' }}>
      <form
        onSubmit={(e) => e.preventDefault()}
        className='flex flex-column items-end'
      >
        <div className='py-4 p-2 grid grid-cols-1 min-[500px]:grid-cols-2 min-[800px]:grid-cols-4 gap-x-4'>
          {changedPlannerIds.map((id, index) => {
            return (
              <div
                key={`${id}-${index}`}
                className='flex basis-1/4 space-x-2 space-y-0'
              >
                <div>
                  <Checkbox
                    className='mt-[6px]'
                    onCheckedChange={(isChecked) => handleChange(isChecked, id)}
                  />
                </div>
                <label>{id}</label>
              </div>
            );
          })}
        </div>
      </form>
      <Button
        className='mt-3 float-right right-12'
        onClick={onSubmit}
        isLoading={isLoading}
        disabled={isLoading}
      >
        Submit for approval
      </Button>
    </div>
  );
}
